import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentPortfolioSettingsData,
  type EnrollmentPortfolioSettingsSubmitInput,
  type EnrollmentPortfolioSettingsSubmitOutput,
} from '../../../models'
import { createMutationHook, createQueryHook } from '../../../hooks'

/**
 * Get Enrollment PortfolioSettings data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentPortfolioSettingsData>
 * @example const response = await getEnrollmentPortfolioSettingsData()
 */
export const getEnrollmentPortfolioSettingsData = async (
  context?: DefaultContext,
): Promise<EnrollmentPortfolioSettingsData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentPortfolioSettingsData',
      query: `mutation getEnrollmentPortfolioSettingsData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
           enrollmentData {
                experimentId
                applicationId
                userId
                eSessionId
                userInfo {
                    employmentStatus
                    occupation
                    netWorthRange
                    income
                    investment {
                        riskTolerance {
                            type
                            allocation {
                                equityPercent
                                fixedIncomePercent
                                cashEquivalentPercent
                            }
                            recommendedRiskTolerance
                            recommendedIndex
                            allowedRiskTolerances
                        }
                        equitySectorAllocations {
                            type
                            maxAllocationPercent
                            currentAllocationPercent
                            recommendedRange {
                                recommendedPercent
                                allowedMinPercent
                                allowedMaxPercent
                            }
                        }
                        fixedIncomeSectorAllocations {
                            type
                            maxAllocationPercent
                            currentAllocationPercent
                            recommendedRange {
                                recommendedPercent
                                allowedMinPercent
                                allowedMaxPercent
                            }
                        }
                        cashEquivalent {
                            current
                            recommended
                            allowTripleTaxFreeMunicipalMutualFund
                        }
                    }
                }
                investmentProfile {
                    objective
                    experience
                    riskTolerance
                    fpslParticipation
                    taxFilingStatus
                    disclosure {
                        customerAccountAgreement {
                            accepted
                        }
                        termsOfUse {
                            accepted
                        }
                        privacyPolicy {
                            accepted
                        }
                        rule14b {
                            accepted
                        }
                        fpslDisclosure {
                            accepted
                        }
                        fpslMasterSecuritiesLendingAgreement {
                            accepted
                        }
                        marginAgreement {
                            accepted
                        }
                    }
                    account {
                        brokerage {
                            accountNickname
                        }
                    }
                }
                recommendation {
                    investment {
                        riskTolerance {
                            recommendedRiskTolerance
                            allowedRiskTolerances
                            riskTolerances {
                                category
                                index
                                allocation {
                                    equityPercent
                                    fixedIncomePercent
                                    cashEquivalentPercent
                                }
                            }
                        }
                        equitySector {
                            type
                            maxAllocationPercent
                            recommendedRange {
                                recommendedPercent
                                allowedMinPercent
                                allowedMaxPercent
                            }
                        }
                        fixedIncomeCategory {
                            type
                            maxAllocationPercent
                            recommendedRange {
                                recommendedPercent
                                allowedMinPercent
                                allowedMaxPercent
                            }
                        }
                        cashEquivalentType {
                            recommended
                            allowTripleTaxFreeMunicipalMutualFund
                        }
                        account {
                            brokerage {
                                accountNickname
                            }
                        }
                    }
                }
                achAuthorization {
                    id
                    version
                    accepted
                    recordedTimestamp
                }
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentPortfolioSettingsData }
  return response.submitEnrollment
}

export const useGetEnrollmentPortfolioSettingsData = createQueryHook<undefined, EnrollmentPortfolioSettingsData>(
  getEnrollmentPortfolioSettingsData,
)

/**
 * Submit Enrollment PortfolioSettings data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentPortfolioSettingsSubmitOutput>
 * @example const response = await submitEnrollmentPortfolioSettingsData();
 */
export const submitEnrollmentPortfolioSettingsData = async (
  enrollmentPortfolioSettingsSubmitInput: EnrollmentPortfolioSettingsSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentPortfolioSettingsSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentPortfolioSettingsData',
      query: `mutation submitEnrollmentPortfolioSettingsData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentPortfolioSettingsSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentPortfolioSettingsSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentPortfolioSettingsData = createMutationHook<
  EnrollmentPortfolioSettingsSubmitInput,
  EnrollmentPortfolioSettingsSubmitOutput
>(submitEnrollmentPortfolioSettingsData)
